<script setup lang="ts">
import {type Component, computed, type PropType} from 'vue';
import {LoaderCircle} from 'lucide-vue-next';

// an icon-only control: the label is what a screen reader and the tooltip say, so it is required
const props = defineProps({
    icon    : {type: [Object, Function] as PropType<Component>, required: true}, // a Lucide icon is a functional component
    label   : {type: String, required: true},
    variant : {type: String as PropType<'default' | 'primary' | 'danger'>, default: 'default'},
    size    : {type: String as PropType<'sm' | 'md'>, default: 'md'},
    disabled: {type: Boolean, default: false},
    loading : {type: Boolean, default: false},
});
const emit = defineEmits<{ click: [event: MouseEvent] }>();

const VARIANT_CLASS: Record<string, string> = {
    default: 'text-zzc-muted hover:bg-zzc-lichtgrijs hover:border-zzc-rand hover:text-zzc-antraciet',
    primary: 'bg-zzc-blauw text-white hover:bg-[#22365a]',
    danger : 'text-zzc-danger hover:bg-[var(--zzc-tag-bad-bg)]',
};
const SIZE_CLASS: Record<string, string>    = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
};

const classes  = computed<string>(() => `${VARIANT_CLASS[props.variant]} ${SIZE_CLASS[props.size]}`);
const iconSize = computed<number>(() => props.size === 'sm' ? 14 : 16);

function onClick(event: MouseEvent) {
    if (!props.disabled && !props.loading) {
        emit('click', event);
    }
}
</script>

<template>
    <button
        type="button"
        class="inline-flex items-center justify-center rounded-control border border-transparent cursor-pointer disabled:opacity-40 disabled:cursor-default transition-colors"
        :class="classes"
        :title="label"
        :aria-label="label"
        :disabled="disabled || loading"
        @click="onClick"
    >
        <LoaderCircle v-if="loading" :size="iconSize" :stroke-width="2" class="animate-spin"/>
        <component v-else :is="icon" :size="iconSize" :stroke-width="2"/>
    </button>
</template>
